import * as React from 'react';

import { cn } from '@heidi/utils';
import { Button } from '../button';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { getOutlineActionButtonPropsExperimental } from './toast-action-button.experimental.styles';

import {
  getOutlineActionButtonProps,
  getSolidActionButtonProps,
} from './toast-action-button.styles';
import type { ToastActionButtonProps } from './toast-action-button.types';

/**
 * Canonical action button for use in the `action` slot of a `<Toast>` /
 * `toast()`. Encapsulates the per-variant / per-tone Button styling so that
 * callers don't have to think about it. The `toastVariant` prop mirrors the
 * parent toast's `variant` and defaults to `outline` to match the toast
 * default — pass `toastVariant="solid"` only when the parent toast is also
 * explicitly solid.
 *
 * @example
 *   toast({
 *     tone: 'success',
 *     title: 'Note saved',
 *     action: (
 *       <ToastActionButton tone="success" onClick={handleUndo}>
 *         Undo
 *       </ToastActionButton>
 *     ),
 *   });
 */
const ToastActionButton = React.forwardRef<HTMLButtonElement, ToastActionButtonProps>(
  ({ tone = 'default', toastVariant = 'outline', className, children, ...rest }, ref) => {
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const recipe =
      toastVariant === 'outline'
        ? (isExperimentalForms
            ? getOutlineActionButtonPropsExperimental
            : getOutlineActionButtonProps)(tone)
        : getSolidActionButtonProps(tone);

    return (
      <Button
        ref={ref}
        type="button"
        size="xxs"
        variant={recipe.variant}
        className={cn(recipe.className, className)}
        {...rest}
      >
        {children}
      </Button>
    );
  }
);
ToastActionButton.displayName = 'ToastActionButton';

export { ToastActionButton };
